import { Alert, Toggle } from '@designsforhealth/dfh-react-components'
import { CircularProgress } from '@material-ui/core'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'

import {
  CommissionsPayoutMode,
  PutCustomerSettingsPractitionerDocument,
} from '../../../../../../graphql/camel'
import useCamelMutation from '../../../../../../hooks/useCamelMutation'
import useSubmitFormOnDirty from '../../../../../../hooks/useSubmitFormOnDirty'
import { PractitionerSettings } from '../../../../../../lib/practitioner-settings/types'
import { tierFromRewardsLevel } from '../../../../../../lib/rewards/levels'
import { logAndCaptureException } from '../../../../../../utils/errorTools'
import { checkAndDiscardQueryResponse } from '../../../../../../utils/graphqlTools'
import { MutationErrorAlert } from '../../../../../MutationErrorAlert'
import Spacer from '../../../../../Spacer'
import { RewardsInformationTooltip } from '../../../rewards/RewardsInformationTooltip'
import { ConfirmationModal } from '../ConfirmationModal'
import { ToggleProgress } from '../ToggleProgress'
import * as Styled from './styled'

export interface PayoutsProgramProps {
  settings: PractitionerSettings
}

const StyledContainer = styled.div`
  p {
    font-size: 14px;
    line-height: 16px;
  }
  margin-bottom: 1rem;
`

const PayoutsProgramToggle = styled(Toggle)`
  && {
    max-width: 350px;
    input:checked + label .slider {
      background-color: ${({ theme }) => theme.colors.royalBlue.shade500};
    }
    label > span {
      display: flex;
      align-items: center;
      // increase width
      flex: 1;
    }
  }
`

const StyledLabel = styled(Styled.Label)`
  display: flex;
  align-items: center;
  flex: 1;
  margin: 0;
`

interface FormValues {
  commissionsPayoutModeAutomatic: boolean
}

export const PayoutsProgram: React.FC<PayoutsProgramProps> = ({ settings }) => {
  const { currentRewardsLevel } = settings.practitioner
  const alertRef = useRef<HTMLDivElement>(null)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [alertVisible, setAlertVisible] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState(false)

  const [putCustomerSettingsPractitioner] = useCamelMutation(
    PutCustomerSettingsPractitionerDocument,
  )

  const { control, errors, formState, handleSubmit, register, reset, setError, watch } =
    useForm<FormValues>({ mode: 'onChange' })

  const onSubmit = useCallback(
    async ({ commissionsPayoutModeAutomatic }: FormValues) => {
      const result = await putCustomerSettingsPractitioner({
        variables: {
          customerSettingsPractitionerUpdateInput: {
            commissionsPayoutMode: commissionsPayoutModeAutomatic
              ? CommissionsPayoutMode.Automatic
              : CommissionsPayoutMode.Manual,
          },
        },
      })
        .then(checkAndDiscardQueryResponse)
        .then(() => ({ error: undefined }))
        .catch((err) => {
          const error = err instanceof Error ? err : new Error(`${err}`)
          logAndCaptureException(error)
          return { error }
        })
      if (result.error) {
        setError('commissionsPayoutModeAutomatic', {
          message: result.error.message,
          type: 'server',
        })
      }
    },
    [putCustomerSettingsPractitioner, setError],
  )

  const { resetForm } = useSubmitFormOnDirty({
    defaultValues: useMemo(
      () => ({
        commissionsPayoutModeAutomatic:
          settings.practitioner.commissionsPayoutMode === CommissionsPayoutMode.Automatic,
      }),
      [settings],
    ),
    formState,
    handleSubmit,
    onSubmit,
    reset,
    submitOnDirty: settings.practitioner.commissionsPayoutMode === CommissionsPayoutMode.Automatic,
  })
  const currentCommissionsPayoutModeAutomatic = watch('commissionsPayoutModeAutomatic')

  const previousRewardsTier = currentRewardsLevel
    ? tierFromRewardsLevel(currentRewardsLevel)
    : undefined
  // tier 3 required to switch to monthly automatic payouts
  const automaticPayoutsDisabled =
    !previousRewardsTier || (previousRewardsTier < 3 && !currentCommissionsPayoutModeAutomatic)

  useEffect(() => {
    if (formState.isSubmitting) {
      return
    }
    if (currentCommissionsPayoutModeAutomatic && formState.isDirty) {
      setShowConfirmation(true)
    } else {
      // close modal when no longer needed
      setShowConfirmation(false)
    }
  }, [currentCommissionsPayoutModeAutomatic, formState.isDirty, formState.isSubmitting])

  const resetAndHideModal = () => {
    setShowConfirmation(false)
    resetForm()
  }

  const handleDisabledClick = () => {
    setAlertVisible(true)
  }

  const handleMouseOver = () => {
    setTooltipOpen(true)
  }

  const handleMouseOut = () => {
    setTooltipOpen(false)
  }

  useEffect(() => {
    if (alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [])

  const hasServerError =
    !!errors.commissionsPayoutModeAutomatic &&
    errors.commissionsPayoutModeAutomatic.type === 'server'

  return (
    <StyledContainer>
      <form onSubmit={handleSubmit(onSubmit)}>
        {hasServerError && (
          <div ref={alertRef}>
            <MutationErrorAlert onCancel={resetForm} onRetry={handleSubmit(onSubmit)}>
              An error occurred updating your settings, and we have been notified.
            </MutationErrorAlert>
          </div>
        )}
        <div
          onClick={automaticPayoutsDisabled ? handleDisabledClick : undefined}
          onMouseOver={automaticPayoutsDisabled ? handleMouseOver : undefined}
          onMouseOut={handleMouseOut}
        >
          <PayoutsProgramToggle
            disabled={formState.isSubmitting || automaticPayoutsDisabled || hasServerError}
            id="commissionsPayoutModeAutomatic"
            name="commissionsPayoutModeAutomatic"
            register={register}
            control={control}
            defaultChecked={
              settings.practitioner.commissionsPayoutMode === CommissionsPayoutMode.Automatic
            }
          >
            <StyledLabel>
              Automatic payouts
              <RewardsInformationTooltip
                placement="top"
                forceOpen={automaticPayoutsDisabled ? tooltipOpen : undefined}
              >
                Tier 3 required to switch to monthly automatic payouts when earnings are over $200.
              </RewardsInformationTooltip>
            </StyledLabel>
            <ToggleProgress isSubmitting={formState.isSubmitting} />
          </PayoutsProgramToggle>
        </div>
        <Spacer size={2} />
        {alertVisible && (
          <Alert type="attention">
            Tier 3 required to switch to monthly automatic payouts when earnings are over $200.
          </Alert>
        )}
        <ConfirmationModal
          title="Change payout mode"
          description="This change might not be applicable to the current payout in process and may take up to 72 hours to take effect. Are you sure you want to proceed with the change?"
          confirmButtonText={
            <>{formState.isSubmitting && <CircularProgress size={16} />} Switch to Automatic</>
          }
          disabled={formState.isSubmitting}
          isOpen={showConfirmation}
          onCancel={resetAndHideModal}
          onConfirm={handleSubmit(onSubmit)}
        >
          {hasServerError && showConfirmation && (
            <MutationErrorAlert onCancel={resetForm} onRetry={handleSubmit(onSubmit)}>
              An error occurred updating your settings, and we have been notified.
            </MutationErrorAlert>
          )}
        </ConfirmationModal>
      </form>
    </StyledContainer>
  )
}
